/**
 * Simple PDF Generator Utility
 * Uses jsPDF to directly generate PDFs without any HTML conversion
 */
import { jsPDF } from 'jspdf';

/**
 * Convert markdown to simple plain text for PDF rendering
 * (Simplified version without HTML conversion)
 */
const markdownToPlainText = (markdown) => {
  if (!markdown) return '';
  
  // Convert markdown to plain text with some basic formatting
  return markdown
    // Remove image links
    .replace(/!\[.*?\]\(.*?\)/g, '')
    // Convert links to text
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    // Replace horizontal rules with lines
    .replace(/^---+$/gm, '-----------------------------------------------------')
    // Add spaces after headings
    .replace(/^(#+)\s+(.*)$/gm, (match, hashes, text) => {
      const level = hashes.length;
      return '\n' + text.toUpperCase() + '\n';
    })
    // Clean up extra whitespace but preserve paragraphs
    .replace(/\n\s+\n/g, '\n\n')
    .trim();
};

/**
 * Download content as PDF using jsPDF
 * @param {string} content - Markdown content
 * @param {string} title - Document title
 */
export const downloadAsPdf = (content, title) => {
  console.log('Using jsPDF for direct PDF generation');
  
  try {
    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Set basic formatting properties
    doc.setFont('helvetica');
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    
    // Add title
    doc.text(title, 20, 20);
    
    // Add metadata line
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 30);
    
    // Add horizontal line
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 35, 190, 35);
    
    // Convert content to plain text
    const plainText = markdownToPlainText(content);
    
    // Add content
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    
    // Word wrap and split into lines for content
    const textLines = doc.splitTextToSize(plainText, 170);
    
    // Add text with line wrapping
    let y = 45; // Starting y position
    const lineHeight = 7; // Line height in mm
    
    textLines.forEach(line => {
      // Check if we need to add a new page
      if (y > 270) {
        doc.addPage();
        y = 20; // Reset y position for new page
      }
      
      // Add the line
      doc.text(line, 20, y);
      y += lineHeight;
    });
    
    // Add footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Generated using document export functionality - Page ${i} of ${pageCount}`, 20, 285);
    }
    
    // Save the PDF
    const filename = `${title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
    doc.save(filename);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF with jsPDF:', error);
    
    // Fallback to plain text download if PDF generation fails
    try {
      console.log('Falling back to text download');
      
      // Create a plain text version of the content
      const plainTextContent = `
${title}

Generated on ${new Date().toLocaleDateString()}

${content}

Generated using document export functionality
      `;
      
      // Create a blob with the text content
      const blob = new Blob([plainTextContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^a-z0-9]/gi, '_')}.txt`;
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);
    } catch (fallbackError) {
      console.error('Error with fallback method:', fallbackError);
    }
    
    return false;
  }
};